import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LockIcon from "@mui/icons-material/Lock";
import { useState, useEffect } from "react";

const PANEL_WIDTH = 300;

export default function SidePanel({ open, title, items, onClose, onNavigate, activeMenu }) {
  const [drillStack, setDrillStack] = useState([]);

  // Reset drill stack when menu changes or panel closes
  useEffect(() => {
    if (!open) setDrillStack([]);
  }, [open, activeMenu]);

  const currentItems = drillStack.length > 0
    ? drillStack[drillStack.length - 1].items
    : items;

  const currentTitle = drillStack.length > 0
    ? drillStack[drillStack.length - 1].label
    : title;

  const handleDrillIn = (item) => {
    setDrillStack(prev => [...prev, { label: item.label, items: item.children }]);
  };

  const handleDrillBack = () => {
    setDrillStack(prev => prev.slice(0, -1));
  };

  const handleClose = () => {
    setDrillStack([]);
    onClose();
  };

  if (!items || items.length === 0) return null;

  const hasPremium = () => true; // TEMPORARY

  const handleItemClick = (item) => {
    if (!item || item === "divider") return;
    if (item.path && item.children) {
      onNavigate(item.path);
      handleClose();
      return;
    }

    // Drill-down item — go deeper
    if (item.children && item.children.length > 0) {
      handleDrillIn(item);
      return;
    }

    const isPremium = item.tier === "premium" || item.tier === "provider-pro";
    const hasAccess = !isPremium || hasPremium();

    // Copilot preload
    if (item.copilotPrompt) {
      window.dispatchEvent(new CustomEvent('copilot-preload', {
        detail: { prompt: item.copilotPrompt }
      }));
      handleClose();
      return;
    }

    if (hasAccess) {
      onNavigate(item.path);
      handleClose();
    } else {
      alert("This feature requires a Premium subscription. Upgrade to access!");
    }
  };

  const renderItems = (itemList) => {
    return itemList.map((item, index) => {
      if (item === "divider") {
        return <Divider key={`divider-${index}`} sx={{ my: 0.5 }} />;
      }

      // Section label (no path, no children — just a label)
      if (item.sectionLabel) {
        return (
          <Box key={`section-${index}`} sx={{ px: 2, pt: 2, pb: 0.5 }}>
            <Typography variant="caption" sx={{
              fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase",
              letterSpacing: 1, color: "text.disabled",
            }}>
              {item.sectionLabel}
            </Typography>
          </Box>
        );
      }

      const isPremium = item.tier === "premium" || item.tier === "provider-pro";
      const hasAccess = !isPremium || hasPremium();
      const hasDrillDown = item.children && item.children.length > 0;

      return (
        <ListItem
          key={item.path || item.label || index}
          button
          onClick={() => handleItemClick(item)}
          sx={{
            borderRadius: 2, mb: 0.5, px: 2, py: 1.25,
            cursor: "pointer", opacity: hasAccess ? 1 : 0.65,
            "&:hover": {
              bgcolor: "rgba(26, 94, 168, 0.08)",
              "& .side-panel-icon": { color: "#1A5EA8" },
              "& .drill-arrow": { opacity: 1, transform: "translateX(2px)" },
            },
            transition: "all 0.15s ease",
          }}
        >
          {item.icon && (
            <ListItemIcon className="side-panel-icon" sx={{ color: "#2D8FD5", minWidth: 40, transition: "color 0.15s ease" }}>
              {item.icon}
            </ListItemIcon>
          )}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.9rem", color: "text.primary" }}>
                {item.label}
              </Typography>
              {item.badge && (
                <Chip label={item.badge} size="small" sx={{
                  height: 18, fontSize: "0.6rem",
                  bgcolor: "rgba(26, 94, 168, 0.1)", color: "#1A5EA8", fontWeight: 600,
                }} />
              )}
              {!hasAccess && <LockIcon sx={{ fontSize: 12, color: "#2D8FD5", ml: "auto" }} />}
            </Box>
            {item.description && (
              <Typography variant="caption" sx={{
                color: "text.secondary", display: "block",
                mt: 0.25, fontSize: "0.75rem", lineHeight: 1.4,
              }}>
                {item.description}
              </Typography>
            )}
            {hasDrillDown && !item.description && (
              <Typography variant="caption" sx={{ color: "#2D8FD5", fontSize: "0.7rem", fontWeight: 600 }}>
                {item.children.length} types
              </Typography>
            )}
          </Box>
          {hasDrillDown && (
            <ArrowForwardIosIcon
              className="drill-arrow"
              onClick={(e) => {
                e.stopPropagation();
                handleDrillIn(item);
              }}
              sx={{
                fontSize: 11, color: "#2D8FD5",
                opacity: 0.45, flexShrink: 0, ml: 1,
                cursor: "pointer", transition: "all 0.15s ease",
                "&:hover": { opacity: 1, transform: "translateX(2px)" },
              }}
            />
          )}
        </ListItem>
      );
    });
  };

  return (
    <>
      {/* Overlay — click outside to close */}
      {open && (
        <Box onClick={handleClose} sx={{ position: "fixed", inset: 0, zIndex: 1199, bgcolor: "transparent" }} />
      )}

      {/* Side Panel */}
      <Box sx={{
        position: "fixed",
        top: 64, left: 0,
        width: PANEL_WIDTH,
        height: "calc(100vh - 64px)",
        bgcolor: "background.paper",
        borderRight: "1px solid", borderColor: "divider",
        zIndex: 1200,
        display: "flex", flexDirection: "column",
        transform: open ? "translateX(0)" : `translateX(-${PANEL_WIDTH}px)`,
        transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: open ? "4px 0 24px rgba(11,37,69,0.12)" : "none",
        overflowY: "auto",
      }}>

        {/* Header */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2, py: 1.5,
          borderBottom: "1px solid", borderColor: "divider",
          background: "linear-gradient(135deg, #0B2545 0%, #1A5EA8 100%)",
          position: "sticky", top: 0, zIndex: 1, minHeight: 52,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
            {drillStack.length > 0 && (
              <IconButton onClick={(e) => { e.stopPropagation(); handleDrillBack(); }} size="small"
                sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "rgba(255,255,255,0.1)", color: "white" }, p: 0.5, flexShrink: 0 }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Box sx={{ minWidth: 0 }}>
              {drillStack.length > 0 && (
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.65rem", display: "block", lineHeight: 1 }}>
                  {title}
                </Typography>
              )}
              <Typography variant="subtitle1" sx={{
                fontWeight: 700, fontSize: "0.95rem", color: "white",
                textTransform: "capitalize", letterSpacing: 0.5,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {currentTitle}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small"
            sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "rgba(255,255,255,0.1)", color: "white" }, flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Breadcrumb */}
        {drillStack.length > 0 && (
          <Box sx={{
            px: 2, py: 0.75,
            bgcolor: "rgba(26,94,168,0.05)",
            borderBottom: "1px solid", borderColor: "divider",
            display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap",
          }}>
            <Typography variant="caption" onClick={() => setDrillStack([])}
              sx={{ color: "#1A5EA8", cursor: "pointer", fontSize: "0.7rem", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
              {title}
            </Typography>
            {drillStack.map((d, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.7rem" }}>›</Typography>
                <Typography variant="caption"
                  onClick={() => i < drillStack.length - 1 && setDrillStack(prev => prev.slice(0, i + 1))}
                  sx={{
                    color: i === drillStack.length - 1 ? "text.primary" : "#1A5EA8",
                    cursor: i === drillStack.length - 1 ? "default" : "pointer",
                    fontSize: "0.7rem",
                    fontWeight: i === drillStack.length - 1 ? 600 : 400,
                    "&:hover": i < drillStack.length - 1 ? { textDecoration: "underline" } : {},
                  }}>
                  {d.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Items */}
        <List sx={{ px: 1.5, py: 1.5, flex: 1 }}>
          {renderItems(currentItems)}
        </List>

        {/* Footer */}
        <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid", borderColor: "divider", bgcolor: "rgba(235,244,252,0.5)" }}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
            🔬 NephroTrax · VeraInsight Healthcare
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export { PANEL_WIDTH };